import { useEffect } from 'react'

import { IMeetDataProps } from '@/src/types/app.type'

import { useBrandingApp } from '@/src/hooks/useBrandingApp'
import { useWindowIsActive } from '@/src/hooks/window/useWindowIsActive'

import { useAppDispatch } from '@/src/store/hooks'
import {
  setBranding,
  setMeetCodes,
  setPrompt,
  setQuestionSections,
  setRoomName,
  setSettings,
} from '@/src/store/slices/meetData'
import { setProject, setAvatar, setV, setRoomType, setReferrer, setPrefilledUsername, setHiddenButtons } from '@/src/store/slices/room'
import { setListTypesMeet } from '@/src/store/slices/typeOfMeet'

export const MeetData = ({
  children,
  settings,
  typesOfMeet,
  meetCodes,
  roomName,
  prompt,
  roomType,
  project,
  avatar,
  v,
  referrer,
  prefilledUsername,
  hiddenButtons,
  ...branding
}: IMeetDataProps) => {
  const dispatch = useAppDispatch()

  useBrandingApp(branding)
  useWindowIsActive()

  useEffect(() => {
    if (settings) {
      dispatch(setSettings(settings))
    }
  }, [settings])

  useEffect(() => {
    if (typesOfMeet) {
      dispatch(setListTypesMeet(typesOfMeet))
    }
  }, [typesOfMeet])

  useEffect(() => {
    if (meetCodes) {
      dispatch(setMeetCodes(meetCodes))
    }
  }, [meetCodes])

  useEffect(() => {
    if (roomName) {
      dispatch(setRoomName(roomName))
    }
  }, [roomName])

  useEffect(() => {
    const normalizedPrompt =
      typeof prompt === 'string' ? prompt : prompt?.assistant || ''
    const questionSections = typeof prompt === 'string' ? [] : prompt?.questionSections || []

    dispatch(setPrompt(normalizedPrompt))
    dispatch(setQuestionSections(questionSections))
  }, [dispatch, prompt])

  useEffect(() => {
    if (roomType) {
      dispatch(setRoomType(roomType))
    }
  }, [roomType])

  useEffect(() => {
    if (project) {
      dispatch(setProject(project))
    }
  }, [project])

  useEffect(() => {
    if (avatar) {
      dispatch(setAvatar(avatar))
    }
  }, [avatar])

  useEffect(() => {
    if (v) {
      dispatch(setV(v))
    }
  }, [v])

  useEffect(() => {
    if (referrer) {
      dispatch(setReferrer(referrer))
    }
  }, [referrer])

  useEffect(() => {
    if (prefilledUsername !== null) {
      dispatch(setPrefilledUsername(prefilledUsername))
    }
  }, [prefilledUsername])

  useEffect(() => {
    if (hiddenButtons) {
      dispatch(setHiddenButtons(hiddenButtons))
    }
  }, [hiddenButtons])

  useEffect(() => {
    if (typeof sessionStorage === 'undefined') {
      return
    }

    if (!branding || Object.keys(branding).length === 0) {
      return
    }
    const existingBranding = JSON.parse(sessionStorage.getItem('brandingData') || '{}')

    const hasNewLogo = branding.logo && branding.logo.trim().length > 0
    const hasExistingLogo = existingBranding.logo && existingBranding.logo.trim().length > 0

    if (hasNewLogo) {
      dispatch(setBranding(branding))
      sessionStorage.setItem('brandingData', JSON.stringify(branding))
    }
    else if (hasExistingLogo) {
      dispatch(setBranding(existingBranding))
    }
    else {
      dispatch(setBranding(branding))
    }

    const userChoices = JSON.parse(localStorage.getItem('lk-user-choices') || '{}')

    if (
      userChoices?.currentBgEffect?.type === 'virtual-background' &&
      branding?.img_background_speaker
    ) {
      const newUserChoices = {
        ...userChoices,
        currentBgEffect: {
          ...userChoices.currentBgEffect,
          options: {
            ...userChoices.currentBgEffect.options,
            imagePath: branding?.img_background_speaker,
          },
        },
      }
      localStorage.setItem('lk-user-choices', JSON.stringify(newUserChoices))
      window.dispatchEvent(new Event('storage'))
    }
  }, [branding])

  useEffect(() => {
    sessionStorage.setItem('apiEndpoint', `${window.location.origin}/api/`)
  }, [])

  return children
}
